import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CdTimerModule } from 'angular-cd-timer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import {
    NzInputDirective,
    NzInputGroupComponent,
    NzInputGroupWhitSuffixOrPrefixDirective,
} from 'ng-zorro-antd/input';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { MetricsService } from '../../../services/metrics.service';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { Line } from '@antv/g2plot';

interface MetricsRow {
    time: string;
    busy: number;
    idle: number;
    open: number;
    qps: number;
    queriesWait: number;
}

@Component({
    selector: 'app-prisma-performance-meter',
    standalone: true,
    imports: [
        CommonModule,
        NzTableModule,
        FormsModule,
        NzFormModule,
        NzInputGroupComponent,
        NzIconDirective,
        NzInputGroupWhitSuffixOrPrefixDirective,
        NzInputDirective,
        CdTimerModule,
        NzProgressModule,
        NzToolTipModule,
        NzCardModule,
        NzButtonModule,
        NzGridModule,
        NzStatisticModule,
        NzTableModule,
    ],
    templateUrl: './prisma-performance-meter.component.html',
    styleUrl: './prisma-performance-meter.component.scss',
})
export class PrismaPerformanceMeterComponent implements AfterViewInit {
    @ViewChild('chartCanvas') chartRef!: ElementRef<HTMLCanvasElement>;
    busy = 0;
    idle = 0;
    open = 0;
    qps = 0;
    queriesWait = 0;
    running = false;
    history: MetricsRow[] = [];
    fullHistory: MetricsRow[] = [];
    private readonly metricsService = inject(MetricsService);
    private plot!: Line;
    private sub!: Subscription;
    private chartData: { metric: string; time: string; value: number }[] = [];

    async ngOnInit() {}

    ngAfterViewInit() {
        this.plot = new Line(document.getElementById('chart-container')!, {
            data: [],
            xField: 'time',
            yField: 'value',
            seriesField: 'metric',
            legend: { position: 'bottom' },
        });
        this.plot.render();
    }
    ngOnDestroy(): void {
        if (this.running) this.sub.unsubscribe();
    }

    startMonitoring(): void {
        if (this.running) {
            return;
        }
        this.running = true;
        this.chartData = [];
        this.plot.changeData(this.chartData);
        this.tick();
        this.sub = interval(2500).subscribe(() => this.tick());
    }

    stopMonitoring(): void {
        if (!this.running) {
            return;
        }
        this.running = false;
        this.sub.unsubscribe();
    }

    downloadLog(): void {
        const header = 'Hora,Busy,Idle,Open,QPS,Wait\n';
        const rows = this.fullHistory
            .map((r) =>
                [r.time, r.busy, r.idle, r.open, r.qps, r.queriesWait].join(
                    ',',
                ),
            )
            .join('\n');
        const blob = new Blob([header + rows], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'prisma_metrics_log.txt';
        a.click();
        window.URL.revokeObjectURL(url);
    }

    private tick(): void {
        this.metricsService.getMetrics$().subscribe({
            next: (data) => {
                this.busy = data.pool.busy ?? 0;
                this.idle = data.pool.idle ?? 0;
                this.open = data.pool.open ?? 0;
                this.qps = data.queries.qps ?? 0;
                this.queriesWait = data.pool.queriesWait ?? 0;
                const now = new Date().toLocaleTimeString();
                const newPoints = [
                    { metric: 'Busy', time: now, value: this.busy },
                    { metric: 'Idle', time: now, value: this.idle },
                    { metric: 'Open', time: now, value: this.open },
                    { metric: 'QPS', time: now, value: this.qps },
                    { metric: 'Wait', time: now, value: this.queriesWait },
                ];
                this.chartData = [...this.chartData, ...newPoints].slice(-150);
                this.plot.changeData(this.chartData);
                this.history.push({
                    time: now,
                    busy: this.busy,
                    idle: this.idle,
                    open: this.open,
                    qps: this.qps,
                    queriesWait: this.queriesWait,
                });
                this.fullHistory.push({
                    time: now,
                    busy: this.busy,
                    idle: this.idle,
                    open: this.open,
                    qps: this.qps,
                    queriesWait: this.queriesWait,
                });
                if (this.history.length > 30) {
                    this.history.shift();
                }
            },
            error: (err) => console.error('Error métricas:', err),
        });
    }
}
